import { useTranslation } from 'react-i18next'
import type { Understanding, UnderstandingSignalKey } from '@/types/coaching'

interface Props {
  lessonCompleted: boolean
  understanding?: Understanding
  isLoading: boolean
}

const SIGNAL_KEYS: UnderstandingSignalKey[] = ['correctRate', 'buildup', 'check']
const RING_RADIUS = 27
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

interface ProgressRingProps {
  label: string
  percent: number | null
  valueLabel: string
  ariaLabel: string
  size: 'overall' | 'signal'
  testId: string
}

function ProgressRing({ label, percent, valueLabel, ariaLabel, size, testId }: ProgressRingProps) {
  const clampedPercent = percent == null ? null : Math.max(0, Math.min(100, percent))
  const dashOffset = clampedPercent == null
    ? RING_CIRCUMFERENCE
    : RING_CIRCUMFERENCE * (1 - clampedPercent / 100)

  return (
    <div
      className={size === 'overall' ? 'flex shrink-0 flex-col items-center gap-1.5' : 'flex min-w-0 flex-col items-center gap-1.5'}
      data-testid={testId}
    >
      <div className={size === 'overall' ? 'relative size-[76px]' : 'relative size-[52px]'}>
        <svg
          className="size-full -rotate-90"
          viewBox="0 0 64 64"
          role="img"
          aria-label={ariaLabel}
        >
          <circle
            cx="32"
            cy="32"
            r={RING_RADIUS}
            fill="none"
            stroke="currentColor"
            strokeWidth="6"
            className="text-lc-lightblue-200"
          />
          {clampedPercent == null ? null : (
            <circle
              cx="32"
              cy="32"
              r={RING_RADIUS}
              fill="none"
              stroke="currentColor"
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              className="text-indigo-500"
            />
          )}
        </svg>
        <span
          className={size === 'overall'
            ? 'absolute inset-0 grid place-items-center text-center text-xs font-extrabold tabular-nums text-gray-950'
            : 'absolute inset-0 grid place-items-center text-center text-[11px] font-bold tabular-nums text-gray-900'}
        >
          {valueLabel}
        </span>
      </div>
      <span className="max-w-[76px] text-center text-[10px] font-semibold leading-tight text-gray-600">
        {label}
      </span>
    </div>
  )
}

export function LessonUnderstandingSummary({ lessonCompleted, understanding, isLoading }: Props) {
  const { t } = useTranslation('coaching')

  if (!lessonCompleted || isLoading || !understanding) {
    return (
      <div
        className="hidden min-h-24 xl:block"
        data-testid="lesson-understanding-slot"
        aria-hidden="true"
      />
    )
  }

  if (understanding.pending || understanding.score == null) {
    return (
      <section
        className="w-full max-w-[360px] rounded-xl border border-lc-lightblue-200 bg-lc-lightblue-100/60 px-3 py-2.5"
        data-testid="lesson-understanding-summary"
      >
        <h3 className="text-xs font-bold text-gray-900">
          {t('coachingGuide.understanding.title')}
        </h3>
        <p className="mt-2 text-xs leading-relaxed text-gray-600">
          {t('coachingGuide.understanding.requiredSignals')}
        </p>
      </section>
    )
  }

  const scorePercent = Math.max(0, Math.min(100, (understanding.score / 5) * 100))
  const breakdownBySignal = new Map(
    understanding.breakdown.map((item) => [item.signal, Math.round(item.normalized * 100)]),
  )

  return (
    <section
      className="w-full max-w-[360px] rounded-xl border border-lc-lightblue-200 bg-[linear-gradient(145deg,var(--color-lc-lightblue-100),white)] px-3 py-2.5 shadow-sm"
      data-testid="lesson-understanding-summary"
    >
      <h3 className="text-xs font-bold text-gray-900">
        {t('coachingGuide.understanding.title')}
      </h3>
      <div className="mt-2 flex items-end gap-3">
        <ProgressRing
          label={t('coachingGuide.understanding.overall')}
          percent={scorePercent}
          valueLabel={t('coachingGuide.understanding.scoreValue', { score: understanding.score })}
          ariaLabel={t('coachingGuide.understanding.scoreValue', { score: understanding.score })}
          size="overall"
          testId="understanding-ring-overall"
        />
        <div className="grid min-w-0 flex-1 grid-cols-3 gap-1.5">
          {SIGNAL_KEYS.map((signal) => {
            const percent = breakdownBySignal.get(signal) ?? null
            const label = t(`coachingGuide.understanding.signal.${signal}`)
            return (
              <ProgressRing
                key={signal}
                label={label}
                percent={percent}
                valueLabel={percent == null ? '–' : `${percent}%`}
                ariaLabel={percent == null
                  ? t('coachingGuide.understanding.signalAriaUnavailable', { label })
                  : t('coachingGuide.understanding.signalAriaValue', { label, value: percent })}
                size="signal"
                testId={`understanding-ring-${signal}`}
              />
            )
          })}
        </div>
      </div>
    </section>
  )
}
